import React, { useReducer } from 'react'
import styles from './index.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { t } from '../../i18n'
import useForm from 'react-hook-form'
import * as yup from 'yup'
import * as api from '../../api/user'
import Error from '../error/Error'
import SpinnerWithText from '../spinnerWithText/SpinnerWithText'
import { navigate } from 'gatsby'
import route from '../../utils/route'
import FormError from '../FormError/FormError'

const NewsLetterSchema = yup.object().shape({
  email: yup
    .string()
    .email(t('validations.register.format', { label: t('formLabels.email') }))
    .required(
      t('validations.register.required', { label: t('formLabels.email') })
    ),
  checkboxSpam: yup
    .boolean()
    .oneOf([true], t('validations.register.accept_spam')),
  checkBoxPrivacy: yup
    .boolean()
    .oneOf([true], t('validations.register.accept_terms')),
})

interface NewsLetterForm {
  email: string
  checkboxSpam: boolean
  checkBoxPrivacy: boolean
}

const initialState = {
  globalError: null as string | null,
  success: false as boolean,
  loading: false as boolean,
}
type State = typeof initialState

function reducer(state: State, action) {
  switch (action.type) {
    case 'SET_NEWSLETTER':
      return {
        ...state,
        loading: true,
      }
    case 'SET_NEWSLETTER_OK':
      return {
        ...state,
        loading: false,
        success: true,
        globalError: null,
      }
    case 'SET_NEWSLETTER_FAILED':
      return {
        ...state,
        loading: false,
        success: false,
        globalError: action.payload.error,
      }
    case 'RESET_SUCCESS':
      return {
        ...state,
        success: false,
      }
    case 'SET_CALLBACK_ERRORS':
      return {
        ...state,
        success: false,
        loading: false,
      }
  }
}

export default function NewsLetter() {
  const [localState, localDispatch] = useReducer(reducer, initialState)
  const {
    register,
    handleSubmit,
    errors,
    setError,
    clearError,
    triggerValidation,
  } = useForm<NewsLetterForm>({
    mode: 'onSubmit',
    validationSchema: NewsLetterSchema,
  })

  const onSubmit = (data) => {
    const path = document.location.hostname
    localDispatch({
      type: 'SET_NEWSLETTER',
    })
    api
      .addToNewsLetter(data.email, path)
      .then((res) => {
        if (res.errors) {
          res.errors.forEach((e) => {
            setError(e.field, e.type, t(e.message))
            localDispatch({
              type: 'SET_CALLBACK_ERRORS',
            })
          })
          return
        }
        localDispatch({
          type: 'SET_NEWSLETTER_OK',
        })
      })
      .catch((err) => {
        if (err.message === 'USER_EXISTS') {
          localDispatch({
            type: 'SET_NEWSLETTER_FAILED',
            payload: {
              error: t('newsletter.ya_existe'),
            },
          })
          return
        }
        localDispatch({
          type: 'SET_NEWSLETTER_FAILED',
          payload: {
            error: t('newsletter.error'),
          },
        })
      })
  }

  if (localState.success) {
    localDispatch({
      type: 'RESET_SUCCESS',
    })
    navigate(route('confirmacion-newsletter'))
  }
  return (
    <form
      onChange={(e) => {
        const target = e.target as HTMLInputElement
        clearError(target.name as keyof NewsLetterForm)
        triggerValidation({
          name: target.name as keyof NewsLetterForm,
        })
      }}
      onSubmit={handleSubmit(onSubmit)}>
      <label
        htmlFor="footer-newsletter-input"
        className={styles.newsletter_input}>
        {t('footer.inscribe')}
      </label>
      <div className={styles.email_input}>
        <input
          id="footer-newsletter-input"
          ref={register}
          name="email"
          className={styles.email_textbox}
          placeholder={t('footer.email')}></input>
        {!localState.loading ? (
          <button
            type="submit"
            className={styles.email_send}
            id={'newsletter'}
            title={t('newsletter.button_title')}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        ) : (
          <div className={styles.spinner}>
            <SpinnerWithText text="" size={20} />
          </div>
        )}
      </div>
      <label className={styles.container}>
        <input ref={register} name="checkboxSpam" type="checkbox" />
        <span
          className={styles.checkmark}
          style={{ width: '20px', height: '20px' }}></span>
        {t('footer.accept1')}
      </label>
      <label className={styles.container}>
        <input ref={register} name="checkBoxPrivacy" type="checkbox" />
        <span
          className={styles.checkmark}
          style={{ width: '20px', height: '20px' }}></span>
        {t('footer.accept2')}
      </label>
      <FormError errors={errors} field="email" />

      {errors.checkboxSpam && <Error message={errors.checkboxSpam.message} />}
      {errors.checkBoxPrivacy && (
        <Error message={errors.checkBoxPrivacy.message} />
      )}
      {localState.globalError && <Error message={localState.globalError} />}
      {localState.success && (
        <p className={styles.successMessage}>{t('newsletter.gracias')}</p>
      )}
    </form>
  )
}
